// Centralised TanStack Query keys so invalidation stays consistent across the
// app. Keys are namespaced per persona/resource.
export const queryKeys = {
	session: ["session"] as const,
	controlPlaneVersion: () => ["control-plane-version"] as const,

	// Tenant-admin (/api/v1/*)
	users: (tenantId: string) => ["tenants", tenantId, "users"] as const,
	user: (tenantId: string, userId: string) =>
		["tenants", tenantId, "users", userId] as const,
	subscriptions: () => ["subscriptions"] as const,
	subscriptionLinks: () => ["subscription-links"] as const,
	tenantPackages: () => ["tenant-packages"] as const,
	distributionEntitlement: () => ["distribution-entitlement"] as const,
	distributionPackages: () => ["distribution-packages"] as const,
	credentials: (userId: string) => ["users", userId, "credentials"] as const,
	tenantNodeGroups: () => ["node-groups"] as const,
	availableEndpoints: () => ["endpoint-selections", "available"] as const,
	usage: (tenantId: string, filters?: Record<string, unknown>) =>
		["tenants", tenantId, "usage", filters ?? {}] as const,

	// Me (/api/v1/ user-scoped views)
	mySubscriptions: (tenantId: string) =>
		["my-subscriptions", tenantId] as const,
	myUsage: (tenantId: string, filter?: string) =>
		["my-usage", tenantId, filter ?? ""] as const,
	traffic: (scope: "me" | "tenant" | "operator", tenantId?: string) =>
		["traffic", scope, tenantId ?? ""] as const,
	trafficHistory: (scope: "operator", window: string, userId = "") =>
		["traffic", scope, "history", window, userId] as const,
	trafficRankings: (window: string) =>
		["traffic", "operator", "rankings", window] as const,
	operatorUserNodeTraffic: (userId: string, window: string) =>
		["traffic", "operator", "users", userId, "nodes", window] as const,
	operatorUserUsage: (tenantId: string, userId: string) =>
		["admin", "tenants", tenantId, "users", userId, "usage"] as const,
	operatorMonthlyTraffic: () =>
		["traffic", "operator", "current-month"] as const,
	myNodeTraffic: (window: "24h" | "7d" | "30d" | "90d") =>
		["traffic", "me", "nodes", window] as const,

	// Me (/api/v1/me/* self-service — see lib/api/me.ts)
	meProfile: () => ["me", "profile"] as const,
	meSubscriptionLinks: () => ["me", "subscription-links"] as const,
	meUsage: () => ["me", "usage"] as const,
	meNodes: () => ["me", "nodes"] as const,
	meCredentials: () => ["me", "credentials"] as const,
	meNotifications: () => ["me", "notifications"] as const,
	meNotificationUnreadCount: () =>
		["me", "notifications", "unread-count"] as const,

	// Operator (/admin/v1/*)
	// Note: "admin" prefix avoids prefix collision with tenant-scoped ["tenants", …] keys.
	tenants: () => ["admin", "tenants"] as const,
	operatorUserCount: () => ["admin", "users", "count"] as const,
	agents: () => ["admin", "agents"] as const,
	agent: (agentId: string) => ["admin", "agents", agentId] as const,
	agentAssignments: (agentId: string) =>
		["admin", "agents", agentId, "assignments"] as const,
	agentTokens: (agentId: string) =>
		["admin", "agents", agentId, "tokens"] as const,
	nodes: () => ["admin", "nodes"] as const,
	adapterTokens: (nodeId: string) =>
		["admin", "nodes", nodeId, "adapter-tokens"] as const,
	globalNodeGroups: () => ["admin", "node-groups"] as const,
	globalNodeGroupMembers: (groupId: string) =>
		["admin", "node-groups", groupId, "members"] as const,
	plans: () => ["admin", "plans"] as const,
	packages: () => ["admin", "packages"] as const,
	operatorTenantPackages: (tenantId: string) =>
		["admin", "tenants", tenantId, "packages"] as const,
	packageDetail: (packageId: string) =>
		["admin", "packages", packageId] as const,
	credentialDomains: () => ["admin", "credential-domains"] as const,
	announcements: () => ["admin", "announcements"] as const,
	operatorUsers: (filters?: Record<string, unknown>) =>
		filters
			? (["admin", "users", filters] as const)
			: (["admin", "users"] as const),
	operatorUserManagers: (tenantId: string) =>
		["admin", "user-managers", tenantId] as const,
	userAccess: (
		scope: "tenant" | "operator",
		tenantId: string,
		userId: string,
	) =>
		[
			scope === "operator" ? "admin" : "tenant",
			"user-access",
			tenantId,
			userId,
		] as const,
	operatorTenantSubscriptions: (tenantId: string) =>
		["admin", "tenants", tenantId, "subscriptions"] as const,
	auditLogs: (filters?: {
		action?: string;
		resourceType?: string;
		outcome?: string;
	}) =>
		filters
			? (["admin", "audit-logs", filters] as const)
			: (["admin", "audit-logs"] as const),
	// Called with no filters as a broad invalidation prefix (matches every
	// filtered variant); called with filters to scope the list query itself.
	grants: (filters?: { tenantId?: string; nodeId?: string }) =>
		filters
			? (["admin", "grants", filters] as const)
			: (["admin", "grants"] as const),
	// Node-scoped managed routing document and heartbeat state. Invalidated
	// when the node's runtime_config is patched.
	runtimeConfig: (nodeId: string) =>
		["admin", "nodes", nodeId, "runtime-config"] as const,
	routingStatus: (nodeId: string) =>
		["admin", "nodes", nodeId, "routing-status"] as const,
	globalRoutingPolicy: () => ["admin", "global-routing-policy"] as const,
	routingResources: (filters?: { kind?: string; status?: string }) =>
		filters
			? (["admin", "routing-resources", filters] as const)
			: (["admin", "routing-resources"] as const),
	routingResourceVersions: (resourceId: string) =>
		["admin", "routing-resources", resourceId, "versions"] as const,
	externalSubscriptions: () => ["admin", "external-subscriptions"] as const,
	externalNodes: () => ["admin", "external-nodes"] as const,
	groupExternalNodes: (groupId: string) =>
		["admin", "node-groups", groupId, "external-nodes"] as const,
	externalSubscriptionNodes: (subscriptionId: string) =>
		["admin", "external-subscriptions", subscriptionId, "nodes"] as const,

	// Traffic routing policies. List key uses a complete filter tuple so global
	// user policies and node overrides never share a cache entry.
	trafficRoutingPolicies: (filters?: {
		tenantId?: string;
		nodeId?: string;
		userId?: string;
		scope?: string;
		includeUserScope?: boolean;
		status?: string;
		cursor?: string;
		limit?: number;
	}) =>
		filters
			? (["admin", "traffic-routing-policies", filters] as const)
			: (["admin", "traffic-routing-policies"] as const),
	trafficRoutingPolicy: (policyId: string) =>
		["admin", "traffic-routing-policies", policyId] as const,
	trafficRoutingPolicyVersions: (tenantId: string, policyId: string) =>
		[
			"admin",
			"traffic-routing-policies",
			policyId,
			"versions",
			tenantId,
		] as const,
	eligiblePolicyUsers: (tenantId: string, nodeId: string) =>
		[
			"admin",
			"traffic-routing-policies",
			"eligible-users",
			tenantId,
			nodeId,
		] as const,
} as const;
